// SETTINGS V2 — nameplate fleet row (B7-FE1, plan §6A.2/§6A.4). One row per
// non-decommissioned machine: the exact public photo, display name, internal
// number, a state pill, the on/off switch (affordance 1), and an expander that
// reveals NameplateRowDetailV2 (affordances 2 + 3).
//
// The thumbnail is the photo the PUBLIC plate would expose — explicit primary
// image, else catalog silhouette, else null. A null thumbnail is correct, not a
// bug: it means no photo would be shown, and the placeholder says so truthfully
// (Security S5). Never substitute a "first ordered file" fallback here.
//
// Both switch directions route through the page's confirm flow — the row never
// writes directly. Inline styles + scoped CSS-var tokens + the SToggle atom. No
// MUI, no dt tokens, no Tailwind.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import type {
  NameplateFleetItem,
  NameplateStatus,
} from "../../../machinery/models/nameplateModels";
import { SToggle } from "../atoms";
import NameplateRowDetailV2 from "./NameplateRowDetailV2";

const ellipsis: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Placeholder for "no public photo will be shown" — a plate outline, not a
 *  stand-in machine photo, so the enable-confirm preview stays honest. */
const PlateSilhouette: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1.8" y="3" width="12.4" height="10" rx="1.4" />
    <path d="M4.4 10.6 L6.6 7.9 L8.4 10 L10 8.6 L11.9 10.6" />
    <circle cx="10.4" cy="6" r="1" />
  </svg>
);

export interface NameplateRowV2Props {
  item: NameplateFleetItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onRequestEnable: (item: NameplateFleetItem) => void;
  onRequestDisable: (item: NameplateFleetItem) => void;
  busy: boolean;
  status?: NameplateStatus;
  statusLoading: boolean;
  domId: string;
  flashing: boolean;
}

const NameplateRowV2: React.FC<NameplateRowV2Props> = ({
  item,
  expanded,
  onToggleExpand,
  onRequestEnable,
  onRequestDisable,
  busy,
  status,
  statusLoading,
  domId,
  flashing,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  const displayName = `${item.manufacturerName || ""} ${item.model || ""}`.trim();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <div
        id={domId}
        // Reuses the generic pulse already defined in settings-v2-tokens.scss
        // (named for its first consumer, the locations map) so this section adds
        // no new shared CSS.
        className={flashing ? "sv2-loc-flash" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: isPhone ? 8 : 12,
          padding: isPhone ? "10px 12px" : "12px 16px",
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          boxShadow: "var(--shadow)",
          minWidth: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Expander — every row has a detail (link + QR, or the no-code hint). */}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={t("settings:nameplates.toggleDetail")}
          style={{
            width: isPhone ? 44 : 28,
            minWidth: isPhone ? 44 : 28,
            height: isPhone ? 44 : 28,
            padding: 0,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg
            viewBox="0 0 12 12"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.18s ease",
            }}
          >
            <path d="M3 4.5 L6 7.5 L9 4.5" />
          </svg>
        </button>

        {/* The exact photo the public plate would expose. */}
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            background: "var(--bg-soft)",
            border: "1px solid var(--border)",
            color: "var(--faint)",
          }}
        >
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <PlateSilhouette />
          )}
        </div>

        {/* Name + type + internal number. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink)",
              minWidth: 0,
              ...ellipsis,
            }}
          >
            {displayName}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--muted)",
              marginTop: 2,
              ...ellipsis,
            }}
          >
            {[item.internalNumber, item.typeName].filter(Boolean).join(" · ")}
          </div>
        </div>

        {/* State pill — public vs off. Hidden on phone (the switch carries it). */}
        {!isPhone && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 8.5,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              flexShrink: 0,
              borderRadius: 999,
              padding: "2px 8px",
              color: item.enabled ? "var(--green)" : "var(--faint)",
              background: item.enabled
                ? "color-mix(in oklab, var(--green) 10%, var(--card))"
                : "var(--bg-soft)",
              border: `1px solid ${
                item.enabled
                  ? "color-mix(in oklab, var(--green) 26%, transparent)"
                  : "var(--border)"
              }`,
            }}
          >
            {item.enabled
              ? t("settings:nameplates.statePublic")
              : t("settings:nameplates.stateOff")}
          </span>
        )}

        {/* Affordance 1 — the switch. Both directions go through a confirm. */}
        <SToggle
          on={item.enabled}
          disabled={busy}
          ariaLabel={t("settings:nameplates.switchAria", { name: displayName })}
          onToggle={(next) => (next ? onRequestEnable(item) : onRequestDisable(item))}
        />
      </div>

      {expanded && (
        <NameplateRowDetailV2
          machineId={item.machineId}
          internalNumber={item.internalNumber}
          status={status}
          loading={statusLoading}
        />
      )}
    </div>
  );
};

export default NameplateRowV2;
